import React from "react"
import { useTranslation } from "react-i18next"

import { MultiLangJobStatus, MultilangJob, displayedName, getColorForJob } from "src/services/api/document"
import BrowserCard from "src/components/browser/card/BrowserCard"
import { CoveringTooltip } from "src/components/browser/card/CoveringTooltip"
import { Typography } from "@mui/material"
import { FileCardLabel } from "./FileCardLabel"
import Deadline from "src/components/browser/typography/Deadline"
import JobButton from "./JobButton"

interface FileCardProps {
  showDownloadButton?: boolean
  showDeadline?: boolean
  job: MultilangJob
  onModalClose?: () => void
  onInfoClick: (job: MultilangJob) => void
  onOpenEmailModal: () => void
}

const FileCard: React.FC<FileCardProps> = ({ showDownloadButton, showDeadline, job, onModalClose, onInfoClick, onOpenEmailModal }) => {
  const { t: tLang } = useTranslation('lang')

  const color = getColorForJob(job)

  const languages = job.singleLangJobs.map(j => j.targetLanguage).sort()
  const languageCodes = languages.join(', ')
  const languageNames = languages.map(j => tLang(j.toLowerCase())).join(', ')

  return <BrowserCard
    key={job.id}
    title={displayedName(job)}
    titleTooltipText={displayedName(job)}
    button={<JobButton job={job} showDownloadButton={showDownloadButton} onModalClose={onModalClose} onOpenEmailModal={onOpenEmailModal} />}
    cardColor={color}
    label={<FileCardLabel job={job} />}
    onInfoClick={() => onInfoClick(job)}
  >
    {showDeadline && job.dueDate && <Deadline dueDate={job.dueDate} />}
    <CoveringTooltip title={languageNames}>
      <Typography variant="body2" sx={{
        width: 'fit-content',
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical"
      }}>{`Languages: ${languageCodes}`}</Typography>
    </CoveringTooltip>
    <JobStatusDescription jobStatus={job.status} />
  </BrowserCard>
}

interface JobStatusDescriptionProps {
  jobStatus: MultiLangJobStatus
}

const JobStatusDescription = ({ jobStatus }: JobStatusDescriptionProps) => {
  const { t } = useTranslation('common')

  const isErrorState = ["CANCELLED"].includes(jobStatus)

  return (isErrorState && <Typography variant="body2">{t(`files.details.${jobStatus}`)}</Typography> || <></>)
}

export default FileCard